// Single source for the backend API base URL.
//
// NEXT_PUBLIC_API_URL usually says http://localhost:5000/api. That is right on the dev machine, but
// for anyone opening the site over the network (e.g. http://192.168.1.20:3000) "localhost" would be
// THEIR own computer. In the browser we therefore swap a loopback host for whatever host the page
// itself was opened on, so the same build works from every device on the network.
const DEFAULT_URL = "http://localhost:5000/api";

const isLoopback = (host: string) => host === "localhost" || host === "127.0.0.1" || host === "::1";

function resolveApiUrl(): string {
  const base = (process.env.NEXT_PUBLIC_API_URL ?? DEFAULT_URL).replace(/\/$/, "");
  // Server-side calls run next to the backend: API_INTERNAL_URL (e.g. http://127.0.0.1:5011/api) skips the public
  // domain / nginx / SSL round trip, which is the usual reason a render sporadically fails on the server.
  if (typeof window === "undefined") return (process.env.API_INTERNAL_URL ?? base).replace(/\/$/, "");
  try {
    const url = new URL(base);
    if (isLoopback(url.hostname) && !isLoopback(window.location.hostname)) {
      url.hostname = window.location.hostname;
      return url.toString().replace(/\/$/, "");
    }
  } catch {
    // malformed env value - fall through to the raw string
  }
  return base;
}

export const API_URL = resolveApiUrl();

const PRIVATE_HOST = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/;

/**
 * Uploaded pictures / videos come back from the API as absolute URLs built from the backend's APP_URL
 * (or "localhost" when the page was rendered on the server). Those break on any device that is not the
 * backend machine, and whenever APP_URL or NEXT_PUBLIC_API_URL changes. Reduce them to the site-relative
 * /images/… or /videos/… path; next.config.ts proxies those to the backend, so they load from anywhere.
 * Anything else (YouTube links, other sites, data: URIs) is returned untouched.
 */
export function mediaPath<T extends string | null | undefined>(src: T): T {
  if (!src) return src;
  try {
    const u = new URL(src);
    const ownHost = u.hostname === new URL(DEFAULT_URL_BASE()).hostname || isLoopback(u.hostname) || PRIVATE_HOST.test(u.hostname);
    if (ownHost && /^\/(images|videos)\//.test(u.pathname)) return (u.pathname + u.search) as T;
  } catch {
    // already a relative path
  }
  return src;
}

function DEFAULT_URL_BASE() {
  return process.env.NEXT_PUBLIC_API_URL ?? DEFAULT_URL;
}
